import React from "react";
import { connect } from "react-redux";
import {
  Layout,
  Table,
  Button,
  Typography,
  Tooltip,
  Row,
  Col,
  Space,
  Modal,
  Dropdown,
  Tag,
  message,
} from "antd";
import {
  cancelIncomes,
  getIncomes,
} from "services/solicitation-service";
import {
  Header,
  FormatDate,
  FormatMoney,
  CardCompanyBalance,
  TableSearch,
  DatePicker,
} from "components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SolicitationPix } from "./solicitation-pix";
import moment from "moment";

import { DownloadOutlined, ExclamationCircleOutlined, FileExcelOutlined, InfoCircleOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import { getAllBenefit } from "services/benefit-service";
import { exportXls, formatDate, formatMoney } from "utils";

const { Footer, Content } = Layout;

const CANCEL_PROCESSING = "P";
const CANCEL_FAILED = "F";
const REFRESH_INTERVAL_MS = 5000;

const isCancelProcessing = (income) => income.cancelStatus === CANCEL_PROCESSING;

const cancelStatusLabel = (item) => {
  if (item.cancelStatus === CANCEL_PROCESSING) return "Cancelando";
  if (item.cancelStatus === CANCEL_FAILED) return "Falha no cancelamento";
  return item.completedAt ? "Finalizado" : "Em Aberto";
};

const incomeToRow = (item, benefits) => {
  const benefitName = benefits?.find((b) => b.benefit === item.benefit)?.name ?? "";
  return {
    Data: formatDate(item.createdAt),
    Empresa: item.company?.name ?? "",
    "CNPJ Empresa": item.company?.cnpj ?? "",
    Participante: item.user?.name ?? "",
    CPF: item.user?.cpf || item.user?.disabledCpf || "",
    "Razão Social": item.user?.businessName ?? "",
    "CNPJ Participante": item.user?.cnpj || item.user?.disabledCnpj || "",
    "Valor do Pedido": formatMoney(item.value, false),
    Benefício: benefitName,
    Status: cancelStatusLabel(item),
  };
};

const mapStateToProps = (state) => ({
  login: state.authReducer.loginReducer,
  balance: state.balanceReducer,
});

const DataFetch = {
  data: [],
  loading: false,
  readStatus: false,
};

const TableCompact = (props) => {
  const [selectByDate, setSelectByDate] = React.useState(false);
  const [date, setDate] = React.useState(() => {
    if (!props.dateRange) return [];
    const parts = props.dateRange.split(":");
    if (parts.length !== 2) return [];
    return [moment(parts[0]), moment(parts[1])];
  });

  const {
    data,
    selectedRowKeys,
    setSelectedRowKeys,
    benefits,
    page,
    pageSize,
    total,
    onPageChange,
    onSearch,
    onDateChange,
    dateRange,
    onSelectionChange,
    onSelectPage,
    onSelectAll,
    selectAllLoading,
    onRefresh,
    lastUpdated,
  } = props;

  const onSelectByDate = async () => {
    setSelectByDate(false);
    if (date && date.length > 0) {
      const startDate = moment(date[0]).format("YYYY-MM-DD");
      const endDate = moment(date[1]).format("YYYY-MM-DD");
      await onDateChange(`${startDate}:${endDate}`);
    } else {
      await onDateChange(null);
    }
  };

  const showBenefit = (benefitId) => {
    const benefit = benefits?.find((item) => item.benefit === benefitId);
    return benefit?.name ?? "";
  };

  return (
    <React.Fragment>
      <Table
        rowKey={"id"}
        rowSelection={{
          selectedRowKeys: selectedRowKeys,
          onChange: onSelectionChange,
          getCheckboxProps: (record) => ({
            disabled: isCancelProcessing(record),
          }),
        }}
        scroll={{ y: 450 }}
        dataSource={data.data}
        loading={data.loading}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showTotal: (t, r) => `${r[0]}-${r[1]} de ${t}`,
          showSizeChanger: true,
          onChange: (newPage, newSize) => {
            onPageChange(newPage, newSize);
          },
        }}
        bordered
        title={() => (
          <Row align={"middle"}>
            <Col xl={16} lg={12} xs={24}>
              <Space wrap>
                <Button size="small" type={"primary"} onClick={onSelectPage}>
                  Selecionar página
                </Button>
                <Button size="small" onClick={onSelectAll} loading={selectAllLoading}>
                  Selecionar todos ({total})
                </Button>
                <Typography.Text>
                  Selecionados: {selectedRowKeys.length}
                </Typography.Text>
                <Button
                  size="small"
                  type={"primary"}
                  onClick={() => setSelectedRowKeys([])}
                >
                  Limpar Todos
                </Button>
                <Button
                  size="small"
                  type={"primary"}
                  onClick={() => setSelectByDate(true)}
                >
                  Buscar por data
                </Button>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={onRefresh}
                  loading={data.loading}
                >
                  Atualizar
                </Button>
                {lastUpdated && (
                  <Typography.Text type="secondary">
                    Atualizado às {moment(lastUpdated).format("HH:mm:ss")}
                  </Typography.Text>
                )}
              </Space>
            </Col>
            <Col xl={8} lg={12} xs={24} className="search-col">
              <TableSearch
                onlyInput
                request={onSearch}
                data={data.data}
                filter={[["company", "name"], ["user", "name"]]}
              />
            </Col>
          </Row>
        )}
      >
        <Table.Column
          width={180}
          align={"center"}
          title="Data"
          dataIndex="createdAt"
          key="createdAt"
          render={(value) => <FormatDate value={value} />}
        />
        <Table.Column
          width={300}
          title="Empresa"
          dataIndex={["company"]}
          key="company"
          render={(value) => (
            <Tooltip
              placement="bottom"
              title={
                <Space direction="vertical">
                  {value.corporateName}
                  {value.cnpj}
                </Space>
              }
            >
              {value.name}
            </Tooltip>
          )}
        />
        <Table.Column
          width={300}
          title="Participante"
          dataIndex={["user"]}
          key="user"
          render={(value) => (
            <Tooltip
              placement="bottom"
              title={
                <Space direction="vertical">
                  {value.name}
                  {value.cpf || value.disabledCpf}
                </Space>
              }
            >
              {value.name}
            </Tooltip>
          )}
        />
        <Table.Column
          width={150}
          title="CPF"
          dataIndex={["user"]}
          key="cpf"
          render={(value) => (
            <Tooltip
              placement="bottom"
              title={
                <Space direction="vertical">
                  {value.cpf || value.disabledCpf}
                </Space>
              }
            >
              {value.cpf || value.disabledCpf}
            </Tooltip>
          )}
        />
        <Table.Column
          width={300}
          title="Razão Social"
          dataIndex={["user"]}
          key="businessName"
          render={(value) => (
            <Tooltip
              placement="bottom"
              title={
                <Space direction="vertical">
                  {value.businessName}
                  {value.cnpj || value.disabledCnpj}
                </Space>
              }
            >
              {value.businessName}
            </Tooltip>
          )}
        />
        <Table.Column
          width={200}
          title="CNPJ"
          dataIndex={["user"]}
          key="cnpj"
          render={(value) => (
            <Tooltip
              placement="bottom"
              title={
                <Space direction="vertical">
                  {value.cnpj || value.disabledCnpj}
                </Space>
              }
            >
              {value.cnpj || value.disabledCnpj}
            </Tooltip>
          )}
        />
        <Table.Column
          width={150}
          align={"right"}
          title="Valor do Pedido"
          dataIndex="value"
          key="value"
          render={(value) => <FormatMoney value={value} />}
        />
        <Table.Column
          width={150}
          align={"center"}
          title={"Benefício"}
          dataIndex={["benefit"]}
          key="benefit"
          render={(benefit) => showBenefit(benefit)}
        />
        <Table.Column
          width={180}
          align={"center"}
          title="Status"
          dataIndex="completedAt"
          key="completedAt"
          render={(value, record) => {
            if (record.cancelStatus === CANCEL_PROCESSING) {
              const retry = record.cancelAttempts > 1 ? `Tentativa ${record.cancelAttempts}` : "Em processamento";
              return (
                <Tooltip title={record.cancelError ? `${retry}: ${record.cancelError}` : retry}>
                  <Tag icon={<SyncOutlined spin />} color="processing">Cancelando</Tag>
                </Tooltip>
              );
            }
            if (record.cancelStatus === CANCEL_FAILED) {
              return (
                <Tooltip title={record.cancelError || "Falha ao cancelar"}>
                  <Tag icon={<ExclamationCircleOutlined />} color="error">Falhou</Tag>
                </Tooltip>
              );
            }
            if (value === null) return "Em Aberto";
            if (value) return "Finalizado";
          }}
        />
      </Table>
      <Modal
        open={selectByDate}
        title="Selecionar pedidos por data"
        onCancel={() => setSelectByDate(false)}
        onOk={onSelectByDate}
      >
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          value={date}
          loading={data.loading}
          disabledDate={(current) => current && current > moment().endOf("day")}
          allowClear={true}
          onChange={(e) => setDate(e)}
        />
      </Modal>
    </React.Fragment>
  );
};

const SolicitationIncomeScreen = (props) => {
  const { login } = props;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = React.useState(DataFetch);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [selectAllLoading, setSelectAllLoading] = React.useState(false);
  const [benefits, setBenefits] = React.useState([]);
  const [showPix, setShowPix] = React.useState({ open: false, qrcode: null });
  const [reload, setReload] = React.useState(false);
  const [total, setTotal] = React.useState(0);
  const [lastUpdated, setLastUpdated] = React.useState(null);

  const page = parseInt(searchParams.get("page") ?? "1");
  const pageSize = parseInt(searchParams.get("pageSize") ?? "10");
  const search = searchParams.get("search") || null;
  const dateRange = searchParams.get("dateRange") || null;

  const syncUrl = React.useCallback((updates) => {
    setSearchParams((prev) => {
      Object.entries(updates).forEach(([k, v]) => {
        if (v != null && v !== "") {
          prev.set(k, String(v));
        } else {
          prev.delete(k);
        }
      });
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const pruneProcessing = (rows) => {
    const processing = new Set(rows.filter(isCancelProcessing).map((x) => x.id));
    if (processing.size === 0) return;
    setSelectedRowKeys((prev) => prev.filter((id) => !processing.has(id)));
  };

  const load = async (filter, range, pg, pgSize) => {
    setData((x) => ({ ...x, loading: true }));
    const res = await getIncomes(filter, false, range, pg, pgSize);
    setData({ data: res.data, loading: false, readStatus: true });
    setTotal(res.total);
    setLastUpdated(new Date());
    pruneProcessing(res.data);
  };

  const hasProcessing = data.data.some(isCancelProcessing);
  const hasProcessingRef = React.useRef(false);
  hasProcessingRef.current = hasProcessing;
  const refreshRef = React.useRef(null);
  refreshRef.current = async () => {
    const res = await getIncomes(search, false, dateRange, page, pageSize, true);
    if (res.error) return;
    setData({ data: res.data, loading: false, readStatus: true });
    setTotal(res.total);
    setLastUpdated(new Date());
    pruneProcessing(res.data);
    if (hasProcessingRef.current && !res.data.some(isCancelProcessing)) {
      setReload((prev) => !prev);
    }
  };

  React.useEffect(() => {
    if (!hasProcessing) return undefined;
    const timer = setInterval(() => refreshRef.current(), REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasProcessing]);

  const handleRefresh = async () => {
    await load(search, dateRange, page, pageSize);
    setReload((prev) => !prev);
  };

  const handleRowSelectionChange = (newPageKeys) => {
    const currentPageIds = new Set(data.data.map((x) => x.id));
    const otherPages = selectedRowKeys.filter((id) => !currentPageIds.has(id));
    setSelectedRowKeys([...otherPages, ...newPageKeys]);
  };

  const handleSelectPage = () => {
    const currentIds = data.data.filter((x) => !isCancelProcessing(x)).map((x) => x.id);
    setSelectedRowKeys((prev) => [...new Set([...prev, ...currentIds])]);
  };

  const handleSelectAll = async () => {
    setSelectAllLoading(true);
    try {
      const res = await getIncomes(search, false, dateRange);
      setSelectedRowKeys(res.data.filter((x) => !isCancelProcessing(x)).map((x) => x.id));
    } finally {
      setSelectAllLoading(false);
    }
  };

  const handleSearch = async (value) => {
    const newSearch = value || null;
    syncUrl({ search: newSearch, page: 1 });
    await load(newSearch, dateRange, 1, pageSize);
  };

  const handleDateChange = async (range) => {
    syncUrl({ dateRange: range, page: 1 });
    await load(search, range, 1, pageSize);
  };

  const handlePageChange = async (newPage, newSize) => {
    syncUrl({ page: newPage, pageSize: newSize });
    await load(search, dateRange, newPage, newSize);
  };

  const loadBenefits = React.useCallback(async () => {
    const benefitsData = await getAllBenefit(login.company.id, true);
    setBenefits(benefitsData);
  }, [login.company.id]);

  const onCancel = (incomes) => {
    Modal.confirm({
      title: "Aviso",
      icon: <InfoCircleOutlined />,
      content: "O saldo desses pedidos retornará ao Saldo Geral. O cancelamento é processado em segundo plano e você acompanha o status na lista. Você tem certeza de que deseja cancelar?",
      onOk: async () => onCancelIncomes(incomes),
      onCancel() {},
    });
  };

  const onExportFiltered = async () => {
    setExportLoading(true);
    try {
      const res = await getIncomes(search, false, dateRange);
      const rows = res.data.map((item) => incomeToRow(item, benefits));
      exportXls().createAndSaveFile(["Cargas"], { Cargas: rows }, "cargas_pendentes_filtradas");
    } catch {
      message.error("Erro ao exportar relatório.");
    } finally {
      setExportLoading(false);
    }
  };

  const onExportAll = () => {
    Modal.confirm({
      title: "Exportar histórico completo",
      icon: <InfoCircleOutlined />,
      content: "Será baixado todo o histórico de cargas, independente de filtros ou status. Dependendo do volume de dados, pode demorar alguns instantes.",
      okText: "Baixar",
      cancelText: "Cancelar",
      onOk: async () => {
        setExportLoading(true);
        try {
          const allData = await getIncomes(null, true, null);
          const rows = allData.data.map((item) => incomeToRow(item, benefits));
          exportXls().createAndSaveFile(["Cargas"], { Cargas: rows }, "cargas_pendentes_todas");
        } catch {
          message.error("Erro ao exportar relatório.");
        } finally {
          setExportLoading(false);
        }
      },
    });
  };

  const onCancelIncomes = async (incomes) => {
    setLoading(true);
    setData((x) => ({ ...x, loading: true }));
    await cancelIncomes(incomes);
    setSelectedRowKeys([]);
    setReload((prev) => !prev);
    syncUrl({ page: 1 });
    await load(search, dateRange, 1, pageSize);
    setLoading(false);
  };

  React.useEffect(() => {
    load(search, dateRange, page, pageSize);
    loadBenefits();
  }, []);

  return (
    <Layout className="alymente-layout">
      <Header title="Pedidos pendentes de Cadastro" routes={[]}>
        <Row gutter={12}>
          <Col xxl={5} xl={8} lg={12}>
            <CardCompanyBalance reload={reload} type={"balance"} />
          </Col>
          <Col xxl={5} xl={8} lg={12}>
            <CardCompanyBalance reload={reload} type={"charge"} />
          </Col>
        </Row>
      </Header>
      <Content>
        <TableCompact
          data={data}
          benefits={benefits}
          setSelectedRowKeys={setSelectedRowKeys}
          selectedRowKeys={selectedRowKeys}
          page={page}
          pageSize={pageSize}
          total={total}
          dateRange={dateRange}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          onDateChange={handleDateChange}
          onSelectionChange={handleRowSelectionChange}
          onSelectPage={handleSelectPage}
          onSelectAll={handleSelectAll}
          selectAllLoading={selectAllLoading}
          onRefresh={handleRefresh}
          lastUpdated={lastUpdated}
        />
        <SolicitationPix
          open={showPix.open}
          pixQRCode={showPix.qrcode}
          onClose={() => setShowPix({ open: false, qrcode: null })}
        />
      </Content>
      <Footer style={{
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
      }}>
        <Button
          disabled={loading}
          loading={loading}
          className="btn-save"
          type="primary"
          onClick={() => navigate("/solicitation")}
        >
          Voltar
        </Button>
        <Space>
          <Dropdown
            menu={{
              items: [
                { key: "filtered", label: "Exportar filtrados", onClick: onExportFiltered },
                { key: "all", label: "Exportar histórico completo", onClick: onExportAll },
              ],
            }}
          >
            <Button icon={<DownloadOutlined />} loading={exportLoading}>
              Exportar Cargas
            </Button>
          </Dropdown>
          <Button
            loading={loading}
            disabled={(selectedRowKeys && selectedRowKeys.length === 0) || loading}
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={() => onCancel(selectedRowKeys)}
          >
            {` Cancelar Cargas (${selectedRowKeys.length})`}
          </Button>
        </Space>
      </Footer>
    </Layout>
  );
};

export default connect(mapStateToProps, {})(SolicitationIncomeScreen);
